import { defineStore } from "pinia";
import { getCustomers, getFrontpage, getArea, getCategory, getLocations, getWarehouse, getItemsList } from '../api/api.js';
import { useAuthStore } from './auth.js';

export const useStore = defineStore('store', {
    state: () => {
        let currentLocation = null;
        let currentWarehouse = null;
        let addToProjectProject = null;
        let addToProjectMode = false;
        try {
            if (localStorage.getItem('location')) currentLocation = JSON.parse(localStorage.getItem('location'));
            if (localStorage.getItem('warehouse')) currentWarehouse = JSON.parse(localStorage.getItem('warehouse'));
            const atp = localStorage.getItem('addToProjectProject');
            if (atp) { addToProjectProject = JSON.parse(atp); addToProjectMode = true; }
        } catch { /* ignore */ }
        return {
            currentLocation,
            currentWarehouse,
            addToProjectMode,
            addToProjectProject,
            menuOpen: false,
            showSearchResult: false,
            startpage: [],
            locations: [],
            warehouses: [],
            areas: [],

            checkoutItemList: [],
            checkoutBadgePulse: false,

            searchResults: [],
            items: [],
            categories: [],
            categoriesLastFetched: null,
            customers: [],
            typeOptions: [
                { value: 1, text: "Unique" },
                { value: 2, text: "MultiItem" },
                { value: 3, text: "Quantity" },
                { value: 4, text: "Consumable" }
            ],
            location: {},
            warehouse: {},
            customer: {},
            event: {},
            area: {},
            item: {},
            item_metadata: {},

            newCategory: {
                name: "",
                parent: null,
            },

            newItem: {
                name: "",
                description: "",
                category: 0,
                item_type: 0,

            },

            newItem_matadata: {
                assetTag: "",
                comment: "",
                serial_number: "",
                area: null,
                item: null,
            },

            category: {},

            // Loading states
            isLoadingItems: false,
            isLoadingLocations: false,
            locationsFetched: false,
            isLoadingWarehouses: false,
            isLoadingCategories: false,
            isLoadingCustomers: false,
            isLoadingArea: false,
            isLoadingFrontpage: false,

            // Error states
            itemsError: null,
            locationsError: null,
            warehousesError: null,
            categoriesError: null,
            customersError: null,
            areaError: null,
            frontpageError: null,

            areaResult: [],
            categoryResult: [],
            customerResult: [],
            eventResult: [],
            itemResult: [],
            item_metadataResult: [],
            locationResult: [],
            warehouseResult: [],
            item_types: ["Undefined", "Unique", "Multi-item", "Quantity", "Consumable"],
        }; // end state return
    }, // end state factory
    actions: {
        enableAddToProject(project) {
            this.addToProjectMode = true;
            this.addToProjectProject = project;
            try { localStorage.setItem('addToProjectProject', JSON.stringify(project)); } catch { /* ignore */ }
        },
        disableAddToProject() {
            this.addToProjectMode = false;
            this.addToProjectProject = null;
            try { localStorage.removeItem('addToProjectProject'); } catch { /* ignore */ }
        },
        addCheckoutItem(item_metadata) {
            if (!item_metadata || item_metadata.id == null) return;
            if (this.checkoutItemList.some(e => !e._isBundle && e.id === item_metadata.id)) {
                this._pulseCheckoutBadge();
                return;
            }
            this.checkoutItemList.push(item_metadata);
            localStorage.setItem('checkoutList', JSON.stringify(this.checkoutItemList));
            this._pulseCheckoutBadge();
        },
        addCheckoutBundle(bundle) {
            if (!bundle || bundle.id == null) return;
            if (this.checkoutItemList.some(e => e._isBundle && e.bundleId === bundle.id)) {
                this._pulseCheckoutBadge();
                return;
            }
            const entry = {
                id: `bundle-${bundle.id}-${Date.now()}`,
                _isBundle: true,
                bundleId: bundle.id,
                name: bundle.name,
                sku: bundle.sku,
                itemCount: bundle.itemCount ?? bundle.items?.length ?? 0,
                availableCount: bundle.availableCount ?? bundle.available_count ?? null,
            };
            this.checkoutItemList.push(entry);
            localStorage.setItem('checkoutList', JSON.stringify(this.checkoutItemList));
            this._pulseCheckoutBadge();
        },
        removeCheckoutItem(item_metadataId) {
            this.checkoutItemList = this.checkoutItemList.filter(metadata => metadata.id != item_metadataId);
            localStorage.setItem('checkoutList', JSON.stringify(this.checkoutItemList));
        },
        _pulseCheckoutBadge() {
            if (this._pulseTimer) clearTimeout(this._pulseTimer);
            this.checkoutBadgePulse = true;
            this._pulseTimer = setTimeout(() => { this.checkoutBadgePulse = false; }, 600);
        },
        loadCheckoutList() {
            const stored = localStorage.getItem('checkoutList');
            if (stored) {
                try {
                    this.checkoutItemList = JSON.parse(stored);
                } catch (error) {
                    console.warn('Failed to parse checkout list from localStorage:', error);
                    this.checkoutItemList = [];
                }
            }
        },
        async fetchFrontpage(force = false) {
            if (!force && (this.isLoadingFrontpage || this.startpage.length > 0)) return;
            const tenantSlug = useAuthStore().currentTenant?.slug;
            if (force) this.startpage = [];
            this.isLoadingFrontpage = true;
            this.frontpageError = null;
            try {
                const cards = await getFrontpage();
                if (useAuthStore().currentTenant?.slug === tenantSlug) this.startpage = cards;
            } catch (error) {
                this.frontpageError = error;
                console.warn('Failed to fetch frontpage:', error);
            } finally {
                this.isLoadingFrontpage = false;
            }
        },
        setCurrentLocation(location) {
            this.currentLocation = location;
            this.currentWarehouse = null;
            this.items = []; // clear cache — warehouse changed
            try {
                if (location) localStorage.setItem('location', JSON.stringify(location));
                else localStorage.removeItem('location');
                localStorage.removeItem('warehouse');
            } catch { /* ignore */ }
            // load warehouses for this location
            if (location?.id) this.fetchWarehouses(location.id);
        },
        async switchTenant(tenantSlug) {
            const authStore = useAuthStore()
            await authStore.switchTenant(tenantSlug)
            this.setCurrentLocation(null);
        },
        setCurrentWarehouse(warehouse) {
            const changed = (warehouse?.id ?? null) !== (this.currentWarehouse?.id ?? null);
            this.currentWarehouse = warehouse;
            if (changed) this.items = []; // clear cache only when warehouse actually changed
            try {
                if (warehouse) localStorage.setItem('warehouse', JSON.stringify(warehouse));
                else localStorage.removeItem('warehouse');
            } catch { /* ignore */ }
        },
        async fetchItems(force = false) {
            if (!force && (this.isLoadingItems || this.items.length > 0)) return;
            this.isLoadingItems = true;
            this.itemsError = null;
            try {
                // Fetch items with server-computed aggregate counts.
                // The API now annotates available_count, total_count,
                // available_quantity_sum, has_downtime, is_unit_available,
                // restock_threshold_val, and areas so we never iterate
                // instances client-side (which breaks under pagination).
                const params = { page_size: 500 };
                if (this.currentWarehouse?.id) params.warehouse = this.currentWarehouse.id;
                else if (this.currentLocation?.id) params.location = this.currentLocation.id;
                const data = await getItemsList(params);
                const items = data.results ?? data ?? [];
                this.items = items.map(it => ({
                    ...it,
                    _areas:               it.areas ?? [],
                    _has_downtime:        it.has_downtime ?? false,
                    // Type 1 — Unique
                    _available:           it.is_unit_available ?? false,
                    // Type 2 — Multi-Item
                    _available_count:     it.available_count ?? 0,
                    _total_count:         it.total_count ?? 0,
                    // Type 3 — Quantity
                    _available_quantity:  it.available_quantity_sum ?? 0,
                    // Type 4 — Consumable
                    _consumable_available: it.available_quantity_sum ?? 0,
                    _restock_threshold:   it.restock_threshold_val ?? null,
                }));
            } catch (error) {
                this.itemsError = error;
                console.warn('Failed to fetch items:', error);
            } finally {
                this.isLoadingItems = false;
            }
        },
        async fetchWarehouse(warehouseId) {
            this.warehousesError = null;
            try {
                const data = await getWarehouse(warehouseId, 0);
                this.warehouse = data;
                await this.fetchArea(data.id);
            } catch (error) {
                this.warehousesError = error;
                console.warn('Failed to fetch warehouse:', error);
            }
        },
        async fetchArea(warehouseId) {
            if (this.isLoadingArea) return;
            this.isLoadingArea = true;
            this.areaError = null;
            try {
                const data = await getArea(warehouseId);
                this.areas = data.results;
            } catch (error) {
                this.areaError = error;
                console.warn('Failed to fetch areas:', error);
            } finally {
                this.isLoadingArea = false;
            }
        },
        async fetchCategories(force = false) {
            const STALE_MS = 60_000 // 60 seconds
            const isStale = !this.categoriesLastFetched || (Date.now() - this.categoriesLastFetched > STALE_MS)
            if (!force && (this.isLoadingCategories || (this.categories.length > 0 && !isStale))) return;
            this.isLoadingCategories = true;
            this.categoriesError = null;
            try {
                const params = {};
                if (this.currentWarehouse?.id) params.warehouse = this.currentWarehouse.id;
                else if (this.currentLocation?.id) params.location = this.currentLocation.id;
                const data = await getCategory(0, { page_size: 500, ...params });
                this.categories = data.results;
                this.categoriesLastFetched = Date.now();
            } catch (error) {
                this.categoriesError = error;
                console.warn('Failed to fetch categories:', error);
            } finally {
                this.isLoadingCategories = false;
            }
        },
        async fetchLocations(force = false) {
            if (!force && (this.isLoadingLocations || this.locations.length > 0)) return;
            this.isLoadingLocations = true;
            this.locationsError = null;
            try {
                const data = await getLocations();
                this.locations = data.results;
            } catch (error) {
                this.locationsError = error;
                console.warn('Failed to fetch locations:', error);
            } finally {
                this.isLoadingLocations = false;
                this.locationsFetched = true;
            }
        },
        async fetchCustomers(force = false) {
            if (!force && (this.isLoadingCustomers || this.customers.length > 0)) return;
            this.isLoadingCustomers = true;
            this.customersError = null;
            try {
                const data = await getCustomers();
                this.customers = data.results;
            } catch (error) {
                this.customersError = error;
                console.warn('Failed to fetch customers:', error);
            } finally {
                this.isLoadingCustomers = false;
            }
        },
        async fetchWarehouses(locationId = 0) {
            if (this.isLoadingWarehouses && !locationId) return;
            this.isLoadingWarehouses = true;
            this.warehousesError = null;
            try {
                const data = await getWarehouse(0, locationId);
                this.warehouses = data.results;
            } catch (error) {
                this.warehousesError = error;
                console.warn('Failed to fetch warehouses:', error);
            } finally {
                this.isLoadingWarehouses = false;
            }
        }
    },
    getters: {
        getCheckoutList(state) {
            return state.checkoutItemList;
        },
        checkoutBundleCount(state) {
            return state.checkoutItemList.filter(i => i._isBundle).length;
        },
        checkoutItemCount(state) {
            return state.checkoutItemList.filter(i => !i._isBundle).length;
        },
        categoryOptions(state) {
            return state.categories.map(cat => ({
                value: cat.id,
                text: cat.name
            }));
        },
        areaOptions(state) {
            return state.areas.map(area => ({
                value: area.id,
                text: area.name
            }));
        }
    },
})